/**
 * CreateGamesStep Component
 * Displays games selection step grouped by league with match rows
 */

import React, { useCallback, useMemo, useRef, useEffect } from 'react'
import { Animated, View, Text } from 'react-native'
import { CreateGamesStepSkeleton } from './CreateGamesStepSkeleton'
import { LeagueSection } from './LeagueSection'
import { SportDivider } from './SportDivider'
import { listSportKeys, getSportDefinitionOrDefault } from '../../../src/sports/sports.constants'

/** Registry order, so adding a sport orders itself without touching this file. */
const SPORT_ORDER = listSportKeys()
const sportRank = (sport?: string | null) => {
  const index = SPORT_ORDER.indexOf(getSportDefinitionOrDefault(sport).key)
  return index === -1 ? SPORT_ORDER.length : index
}
import { useCreatePage } from '../../../src/create/contexts/CreatePageContext'
import { useFavoriteLeagues } from '../../../src/create/hooks/useFavoriteLeagues'
import { useSkeletonLoading } from '../../../src/shared/hooks/useSkeletonLoading'
import { useDeferredListDate } from '../../../src/create/hooks/useDeferredListDate'
import { FadeInContent } from '../../shared/FadeInContent'
import { StaggeredFadeIn } from '../../shared/StaggeredFadeIn'
import { getCreateGamesStepStyles } from './CreateGamesStep.styles'
import { useTheme } from '../../../src/theme/useTheme'
import { useDialog } from '../../../src/shared/hooks/useDialog'
import {
  MAX_GAMES_PER_CHALLENGE,
  LEAGUE_DISPLAY_PRIORITY,
} from '../../../src/create/constants/create.constants'
import { formatLongDate } from '../../../helpers/dateFormatters.helper'
import { isGameNotStarted } from '../../../src/create/helpers/createGames.helper'
import { useWalkthroughStep } from '../../../src/shared/hooks/useWalkthroughStep'
import { useGuestWalkthroughStore } from '../../../src/shared/stores/guestWalkthroughStore'
import { WALKTHROUGH_STEPS } from '../../../src/create/constants/guestWalkthrough.constants'
import type { Game } from '../../../src/create/types/create.types'

const SENTINEL_DISPLAY_ORDER = Number.MAX_SAFE_INTEGER
/** Leagues not in LEAGUE_DISPLAY_PRIORITY sort last (after all prioritized leagues). */
const PRIORITY_INDEX_LAST = 9999
const GAME_DATE_SEPARATOR = 'T'

function toStartOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function getFirstWalkthroughAvailableDate(games: Game[]): Date | null {
  for (const game of games) {
    if (!isGameNotStarted(game) || game.hasRealOdds === false) {
      continue
    }
    const parsedDate = new Date(`${game.date}${GAME_DATE_SEPARATOR}00:00:00`)
    if (!Number.isNaN(parsedDate.getTime())) {
      return toStartOfDay(parsedDate)
    }
  }
  return null
}

function getLeaguePriorityIndex(leagueName: string): number {
  const key = leagueName.trim().toLowerCase()
  const i = LEAGUE_DISPLAY_PRIORITY.indexOf(key)
  return i === -1 ? PRIORITY_INDEX_LAST : i
}

/** Opacity for Storybook / previews when `useLoadingSkeleton` is false (no fade hook dependency). */
const FULL_OPACITY = new Animated.Value(1)

export interface CreateGamesStepProps {
  /**
   * When false, shows the games list area immediately instead of `CreateGamesStepSkeleton` while data loads (e.g. Storybook).
   * @default true
   */
  useLoadingSkeleton?: boolean
}

export const CreateGamesStep: React.FC<CreateGamesStepProps> = ({
  useLoadingSkeleton = true,
}) => {
  const theme = useTheme()
  const styles = useMemo(() => getCreateGamesStepStyles(theme), [theme])
  const { showAlert } = useDialog()
  const { favoriteLeagueIds, isLoading: favoritesLoading, toggleFavorite } = useFavoriteLeagues()

  const {
    selectedDate,
    setSelectedDate,
    games,
    gamesByLeague,
    getPickSlipForGame,
    handlePickSelect,
    pickSlips,
    gamesLoading,
  } = useCreatePage()

  const pickSlipsRef = useRef(pickSlips)
  useEffect(() => {
    pickSlipsRef.current = pickSlips
  }, [pickSlips])

  const leagueEntries = useMemo(() => {
    const entries = Object.entries(gamesByLeague)
    if (favoritesLoading) return entries
    return [...entries].sort(([nameA, gamesA], [nameB, gamesB]) => {
      // Sport first (specs/multi-sport-challenges): the list is grouped sport → leagues → games,
      // so two leagues of different sports must never sit next to each other. Football leads,
      // matching DEFAULT_SPORT, so the existing experience is unchanged for a football-only user.
      const sportRankA = sportRank(gamesA[0]?.sport)
      const sportRankB = sportRank(gamesB[0]?.sport)
      if (sportRankA !== sportRankB) return sportRankA - sportRankB
      const leagueIdA = gamesA[0]?.leagueId
      const leagueIdB = gamesB[0]?.leagueId
      const orderA = gamesA[0]?.leagueDisplayOrder ?? SENTINEL_DISPLAY_ORDER
      const orderB = gamesB[0]?.leagueDisplayOrder ?? SENTINEL_DISPLAY_ORDER
      const favA = leagueIdA != null && favoriteLeagueIds.has(leagueIdA)
      const favB = leagueIdB != null && favoriteLeagueIds.has(leagueIdB)
      if (favA !== favB) return favA ? -1 : 1
      if (orderA !== orderB) return orderA - orderB
      const priorityA = getLeaguePriorityIndex(nameA)
      const priorityB = getLeaguePriorityIndex(nameB)
      if (priorityA !== priorityB) return priorityA - priorityB
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' })
    })
  }, [gamesByLeague, favoritesLoading, favoriteLeagueIds])

  /** Games per sport, for the divider's count. */
  const sportGameCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const [, leagueGames] of leagueEntries) {
      const key = getSportDefinitionOrDefault(leagueGames[0]?.sport).key
      counts[key] = (counts[key] ?? 0) + leagueGames.length
    }
    return counts
  }, [leagueEntries])

  const selectedGameIds = useMemo(
    () => new Set(pickSlips.map((s) => s.gameId)),
    [pickSlips]
  )
  const selectedCount = pickSlips.length

  /** Count of picks (pick slips) per game – shown on each row when user returns from match details. */
  const picksCountByGameId = useMemo(() => {
    const map: Record<string, number> = {}
    for (const slip of pickSlips) {
      const id = slip.gameId
      if (id) map[id] = (map[id] ?? 0) + 1
    }
    return map
  }, [pickSlips])

  const firstGameId = leagueEntries[0]?.[1]?.[0]?.id ?? null
  const isWalkthroughActive = useGuestWalkthroughStore((s) => s.isActive)
  const currentStep = useGuestWalkthroughStore((s) => s.currentStep)
  const advanceWalkthroughStep = useGuestWalkthroughStore((s) => s.advanceStep)
  const { viewRef: firstGameRef } = useWalkthroughStep(WALKTHROUGH_STEPS.GAMES_LIST, {
    useAfterLayout: true,
    isEnabled: !gamesLoading && firstGameId !== null,
    onProxyTap: () => {
      advanceWalkthroughStep()
    },
  })
  const firstWalkthroughAvailableDate = useMemo(
    () => isWalkthroughActive ? getFirstWalkthroughAvailableDate(games) : null,
    [games, isWalkthroughActive]
  )

  // If there are no visible games for the selected date, auto-jump to the first date
  // with available walkthrough-compatible games so users don't get stuck.
  useEffect(() => {
    if (!isWalkthroughActive || currentStep !== WALKTHROUGH_STEPS.GAMES_LIST || gamesLoading) {
      return
    }
    if (leagueEntries.length > 0) return
    if (firstWalkthroughAvailableDate === null) {
      return
    }

    const selectedDateStart = toStartOfDay(selectedDate).getTime()
    const fallbackDateStart = firstWalkthroughAvailableDate.getTime()
    if (selectedDateStart !== fallbackDateStart) {
      setSelectedDate(firstWalkthroughAvailableDate)
    }
  }, [
    isWalkthroughActive,
    currentStep,
    gamesLoading,
    leagueEntries.length,
    selectedDate,
    firstWalkthroughAvailableDate,
    setSelectedDate,
  ])

  const { isLoadingWithDelay, contentOpacity } = useSkeletonLoading({
    isLoading: gamesLoading || favoritesLoading,
    isContentReady: !gamesLoading && !favoritesLoading,
    animationDuration: 150,
  })

  const handlePickSelectWithLimit = useCallback(
    (gameId: string, pick: 'home' | 'draw' | 'away' | null) => {
      if (pick === null) {
        handlePickSelect(gameId, pick)
        return
      }

      const slips = pickSlipsRef.current
      const isAlreadySelected = slips.some((slip) => slip.gameId === gameId)

      if (!isAlreadySelected && slips.length >= MAX_GAMES_PER_CHALLENGE) {
        showAlert({
          title: 'Game Limit Reached',
          message: `You can only add up to ${MAX_GAMES_PER_CHALLENGE} games per challenge. Please remove a game first if you want to add a different one.`,
          type: 'default',
          icon: '⚠️',
        }).catch(() => undefined)
        return
      }

      handlePickSelect(gameId, pick)
    },
    [handlePickSelect, showAlert]
  )

  const isWalkthroughGamesStep = isWalkthroughActive && currentStep === WALKTHROUGH_STEPS.GAMES_LIST
  const handleWalkthroughGameTap = useCallback(() => {
    advanceWalkthroughStep()
  }, [advanceWalkthroughStep])

  // AC-3.7: the strip highlights the new day on this commit; the list renders in a deferred pass.
  const { isSwitchingDate } = useDeferredListDate(selectedDate)

  const showLoadingSkeleton = useLoadingSkeleton && (isLoadingWithDelay || isSwitchingDate)
  const fadeOpacity = useLoadingSkeleton ? contentOpacity : FULL_OPACITY
  const contentKey = showLoadingSkeleton ? 'loading' : 'content'

  return (
    <View key={contentKey} style={styles.stepWrapper}>
      {showLoadingSkeleton ? (
        <CreateGamesStepSkeleton />
      ) : (
        <FadeInContent opacity={fadeOpacity} testID="create-games-step">
          {leagueEntries.length === 0 ? (
            <View style={styles.emptyContainer} testID="create-games-step-empty">
              <Text style={styles.emptyTitle}>
                No games available for {formatLongDate(selectedDate)}
              </Text>
              <Text style={styles.emptySubtitle}>
                Try selecting a different date on the calendar
              </Text>
              <Text style={styles.emptyHint}>
                Total games loaded: {games.length}
              </Text>
            </View>
          ) : (
            leagueEntries.map(([league, leagueGames], index) => {
              const leagueId = leagueGames[0]?.leagueId
              const isFavorite = leagueId != null && favoriteLeagueIds.has(leagueId)
              // Entries are sorted sport-first, so a sport change marks the start of a section.
              const sportKey = getSportDefinitionOrDefault(leagueGames[0]?.sport).key
              const previousSportKey = index === 0
                ? null
                : getSportDefinitionOrDefault(leagueEntries[index - 1][1][0]?.sport).key
              const startsSportSection = sportKey !== previousSportKey
              return (
                <StaggeredFadeIn
                  key={league}
                  index={index}
                  disableAnimation={!useLoadingSkeleton}
                >
                  {startsSportSection && (
                    <SportDivider sport={sportKey} gameCount={sportGameCounts[sportKey] ?? 0} />
                  )}
                  <LeagueSection
                    league={league}
                    leagueId={leagueId}
                    leagueGames={leagueGames}
                    isFavorite={isFavorite}
                    onToggleFavorite={toggleFavorite}
                    getPickSlipForGame={getPickSlipForGame}
                    handlePickSelect={handlePickSelectWithLimit}
                    selectedGameIds={selectedGameIds}
                    selectedCount={selectedCount}
                    picksCountByGameId={picksCountByGameId}
                    walkthroughFirstRowRef={index === 0 ? firstGameRef : undefined}
                    isWalkthroughGamesStep={index === 0 ? isWalkthroughGamesStep : undefined}
                    isWalkthroughCard={index === 0 ? isWalkthroughActive : undefined}
                    onWalkthroughGameTap={index === 0 ? handleWalkthroughGameTap : undefined}
                  />
                </StaggeredFadeIn>
              )
            })
          )}
        </FadeInContent>
      )}
    </View>
  )
}
